import React, { useState } from "react";
import { generateClient } from "aws-amplify/api";
import "../../lib/amplify"; // Import Amplify configuration
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Database, Play, AlertCircle } from "lucide-react";
import {
  listPlayerConstructs,
  listEventPlayerResults,
  listEvents,
} from "../../graphql/queries";

// Use the properly configured Amplify client
const client = generateClient();

const GraphQLTest: React.FC = () => {
  const [results, setResults] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runTest = async () => {
    setIsLoading(true);
    setError(null);
    const testResults: any = {};

    try {
      // First, test basic connectivity
      console.log("Testing GraphQL endpoint connectivity...");
      const endpoint =
        "https://xni4nimt2jhmllo5qm3dxgmz3q.appsync-api.us-east-2.amazonaws.com/graphql";

      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "da2-4je3giakkjanxavqwr2melml7a",
          },
          body: JSON.stringify({
            query: `query { __schema { types { name } } }`,
          }),
        });

        console.log(
          "Connectivity test response:",
          response.status,
          response.statusText
        );
        testResults.connectivity = {
          success: response.ok,
          status: response.status,
          statusText: response.statusText,
        };
      } catch (connectError: any) {
        console.error("Connectivity test failed:", connectError);
        testResults.connectivity = {
          success: false,
          error: connectError.message || "Connection failed",
        };
      }
      // Test PlayerConstructs query
      console.log("Testing PlayerConstructs query...");
      const playerConstructsResult = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 10 },
      });
      testResults.playerConstructs = {
        success: true,
        count: playerConstructsResult.data.listPlayerConstructs.items.length,
        data: playerConstructsResult.data.listPlayerConstructs.items,
        rawResponse: playerConstructsResult,
      };
      console.log("PlayerConstructs result:", playerConstructsResult);

      // Test EventPlayerResults query
      console.log("Testing EventPlayerResults query...");
      const eventPlayerResultsResult = await client.graphql({
        query: listEventPlayerResults,
        variables: { limit: 10 },
      });
      testResults.eventPlayerResults = {
        success: true,
        count:
          eventPlayerResultsResult.data.listEventPlayerResults.items.length,
        data: eventPlayerResultsResult.data.listEventPlayerResults.items,
        rawResponse: eventPlayerResultsResult,
      };
      console.log("EventPlayerResults result:", eventPlayerResultsResult);

      // Test Events query
      console.log("Testing Events query...");
      const eventsResult = await client.graphql({
        query: listEvents,
        variables: { limit: 10 },
      });
      testResults.events = {
        success: true,
        count: eventsResult.data.listEvents.items.length,
        data: eventsResult.data.listEvents.items,
        rawResponse: eventsResult,
      };
      console.log("Events result:", eventsResult);

      setResults(testResults);
    } catch (err: any) {
      console.error("GraphQL test error:", err);
      console.error("Error details:", {
        message: err.message,
        code: err.code,
        name: err.name,
        stack: err.stack,
        errors: err.errors,
        data: err.data,
        extensions: err.extensions,
      });

      const errorMessage =
        err.message || err.errors?.[0]?.message || "Unknown error occurred";
      const errorDetails = {
        message: errorMessage,
        code: err.code,
        name: err.name,
        errors: err.errors,
        data: err.data,
        extensions: err.extensions,
        fullError: err,
      };

      setError(JSON.stringify(errorDetails, null, 2));
      setResults({ error: errorDetails });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <Database className="h-8 w-8 text-primary" />
        <h1 className="text-3xl font-bold">GraphQL Connection Test</h1>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Environment Info</CardTitle>
          <CardDescription>Current Amplify configuration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div>
              <strong>Environment:</strong> Development (dev)
            </div>
            <div>
              <strong>GraphQL Endpoint:</strong>{" "}
              https://xni4nimt2jhmllo5qm3dxgmz3q.appsync-api.us-east-2.amazonaws.com/graphql
            </div>
            <div>
              <strong>Region:</strong> us-east-2
            </div>
            <div>
              <strong>Auth Type:</strong> API_KEY
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Test GraphQL Queries</CardTitle>
          <CardDescription>
            Test the same queries used by the public standings page
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={runTest} disabled={isLoading} className="w-full">
            {isLoading ? "Testing..." : "Run GraphQL Tests"}
          </Button>

          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-md">
              <div className="flex items-center gap-2 text-red-800">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Error:</span>
              </div>
              <p className="text-red-700 mt-1">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {Object.keys(results).length > 0 && (
        <div className="space-y-6">
          {Object.entries(results).map(([key, result]: [string, any]) => (
            <Card key={key}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Play className="h-5 w-5" />
                  {key}
                </CardTitle>
                <CardDescription>
                  {result.success
                    ? `✅ Success - ${result.count} records`
                    : "❌ Failed"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {result.success ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <strong>Count:</strong> {result.count}
                      </div>
                      <div>
                        <strong>Status:</strong> Success
                      </div>
                    </div>

                    {result.data && result.data.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Sample Data:</h4>
                        <Textarea
                          value={JSON.stringify(
                            result.data.slice(0, 2),
                            null,
                            2
                          )}
                          readOnly
                          rows={8}
                          className="font-mono text-xs"
                        />
                      </div>
                    )}

                    {result.data && result.data.length === 0 && (
                      <div className="text-yellow-600 bg-yellow-50 p-3 rounded">
                        <strong>⚠️ No data found</strong> - This table is empty
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-red-600 bg-red-50 p-3 rounded">
                    <strong>❌ Query failed:</strong>{" "}
                    {result.error || "Unknown error"}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p>
              <strong>If PlayerConstructs = 0:</strong> EventLink imports aren't
              creating PlayerConstruct records
            </p>
            <p>
              <strong>If EventPlayerResults = 0:</strong> Event results aren't
              being stored properly
            </p>
            <p>
              <strong>If Events = 0:</strong> No events have been created
            </p>
            <p>
              <strong>If all queries fail:</strong> GraphQL connection issue
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GraphQLTest;
